import { createFocusTrap } from 'focus-trap'
import { navBreakpoint } from '../utils/breakpoints.js'

const BODY = document.body

let focusTraps = []

/**
 * With heavy page caching we need a FE solution here
 */
const activeHeaderItem = (links) => {
    const { origin, pathname } = window.location
    const curPage = origin + pathname
    links.forEach((link) => {
        const { href } = link
        if (!href || href.startsWith('#')) return

        const url = new URL(href, origin)
        if (url.href === curPage) {
            link.classList.add('active-nav-link')
            link.setAttribute('aria-current', 'page')
        }
    })
}

/**
 * Functionality to update `aria-expanded`.
 * @param link
 * @param state
 */
const setAriaExpanded = (link, state) => {
    link.setAttribute('aria-expanded', state)
}

/**
 * A top-level link's dropdown is always its very next sibling, marked with
 * `.js-nav-dropdown` - shared by every piece of code that needs to check
 * whether a given link has one.
 * @param {HTMLElement} link
 * @returns {HTMLElement|null}
 */
const getDropdown = (link) => {
    const dropdown = link.nextElementSibling
    return dropdown && dropdown.classList.contains('js-nav-dropdown') ? dropdown : null
}

/**
 * Fully close a dropdown: hide it, take it out of the tab/AT order, and
 * flag its trigger as collapsed. Shared by every close path (hover-out,
 * desktop close, mobile close, breakpoint change, main menu close) so they
 * can't drift out of sync with each other.
 * @param {HTMLElement} link
 * @param {HTMLElement} dropdown
 */
const closeDropdown = (link, dropdown) => {
    dropdown.dataset.active = 'false'
    dropdown.inert = true
    setAriaExpanded(link, 'false')
}

/**
 * Reset focused states of focused nav items.
 * @param header
 * @param {HTMLElement} [excludeLink] - skip closing this link's own dropdown.
 * Every link's `mouseenter` calls this to close whatever *other* dropdown was
 * open; without the exclusion, hovering a trigger whose menu is already open
 * (by click or ArrowDown) would close and `blur()` it before reopening it.
 */
const resetActiveStates = (header, excludeLink) => {
    const focusedItem = header.querySelector('.js-nav-link[aria-expanded="true"]')
    if (focusedItem && focusedItem !== excludeLink) {
        const dropdown = focusedItem.nextElementSibling
        if (dropdown) closeDropdown(focusedItem, dropdown)
        focusedItem.blur()
    }
    const { activeElement } = document
    if (activeElement.classList.contains('js-nav-link-child')) {
        activeElement.blur()
    }
}

/**
 * Close whatever mega menu is open when the cursor leaves the header or
 * Escape is pressed. Hover opening/closing itself lives in `childMenuToggler`,
 * which owns the open/close functions the click and keyboard paths share.
 * @param header
 * @param links
 */
const accessibleMenuItems = (header, links) => {
    // Clear focused state of focused nav items when hovering off of navigation,
    // unless a keyboard user is still actively focused within it.
    header.addEventListener('mouseleave', () => {
        if (!navBreakpoint.matches) return
        if (header.contains(document.activeElement)) return
        resetActiveStates(header)
    })

    // Clear focused state of focused nav items when hitting escape key.
    document.addEventListener('keydown', (event) => {
        if (!navBreakpoint.matches) return
        if (event.key === 'Escape' || event.keyCode === 27) {
            resetActiveStates(header)
        }
    })

    links.forEach((link) => {
        // Links with a dropdown hover-handle themselves; plain ones still need
        // to dismiss a sibling's open mega menu.
        if (getDropdown(link)) return

        link.addEventListener('mouseenter', () => {
            if (!navBreakpoint.matches) return
            resetActiveStates(header, link)
        })
    })
}

/**
 * Top-level nav links with children.
 *
 * Desktop opens/closes the dropdown via hover, ArrowDown, Space, click, and
 * focusout. On mobile the link's click is intercepted - it never navigates -
 * and instead slides its `.js-nav-dropdown`
 * panel in over the main mobile menu. The panel carries a surfaced copy
 * of this same link (real `href`, so the destination is still reachable)
 * plus a `.js-nav-link-back` button that slides it back out.
 *
 * `mainTrap` is the main mobile menu's own focus trap (from
 * `mobileNavToggler`) - it's paused while this panel's trap is active and
 * unpaused again on the way back out, since two focus traps active over
 * overlapping DOM at once otherwise fight each other over Tab handling.
 *
 * @param header
 * @param links
 * @param {import('focus-trap').FocusTrap|null} mainTrap
 */
const childMenuToggler = (header, links, mainTrap) => {
    links.forEach((link) => {
        const dropdown = getDropdown(link)
        if (!dropdown) return

        const focusTrap = createFocusTrap(dropdown, {
            escapeDeactivates: false,
            allowOutsideClick: true,
            fallbackFocus: dropdown,
            preventScroll: true,
        })
        focusTraps.push(focusTrap)

        const parent = link.parentElement
        const backButton = dropdown.querySelector('.js-nav-link-back')

        // Desktop: explicit open/close, driven by click, ArrowDown, or Space - never
        // by focus alone, so Tab can move straight past a closed trigger to
        // reach the next top-level nav item instead of walking every child.
        const openDesktop = (moveFocusIn) => {
            dropdown.dataset.active = 'true'
            dropdown.inert = false
            setAriaExpanded(link, 'true')
            if (!moveFocusIn) return
            const firstItem = dropdown.querySelector('.js-nav-link-child')
            // preventScroll: every top-level dropdown panel sits stacked in the
            // DOM regardless of which one is actually visible, so focusing into
            // one without it lets the browser's default scroll-into-view drag
            // the whole nav sideways to "reveal" a sibling panel that was never
            // meant to be seen.
            if (firstItem) firstItem.focus({ preventScroll: true })
        }

        const closeDesktop = (returnFocus) => {
            closeDropdown(link, dropdown)
            if (returnFocus) link.focus({ preventScroll: true })
        }

        // Mobile: slide the panel in instead of navigating.
        const openMobile = () => {
            dropdown.dataset.active = 'true'
            dropdown.inert = false
            setAriaExpanded(link, 'true')
            if (mainTrap) mainTrap.pause()
            focusTrap.activate()
            if (backButton) backButton.focus({ preventScroll: true })
        }

        const closeMobile = (returnFocus) => {
            closeDropdown(link, dropdown)
            focusTrap.deactivate()
            if (mainTrap) mainTrap.unpause()
            if (returnFocus) link.focus({ preventScroll: true })
        }

        // Desktop hover opens on the link (the `<li>` spans the whole 86px nav
        // row, so opening off that fired the menu from well above the label)
        // but closes on the `<li>`, which acts as the bridge: it reaches down
        // to `--header-height` where the panel starts, and the panel is a
        // descendant, so the cursor can travel between them without leaving it.
        link.addEventListener('mouseenter', () => {
            if (!navBreakpoint.matches) return
            resetActiveStates(header, link)
            openDesktop(false)
        })

        parent.addEventListener('mouseleave', () => {
            if (!navBreakpoint.matches) return
            // Don't close out from under a keyboard user focused inside.
            if (parent.contains(document.activeElement)) return
            closeDesktop(false)
        })

        link.addEventListener('click', (event) => {
            if (navBreakpoint.matches) {
                const isOpen = dropdown.dataset.active === 'true'
                if (isOpen) {
                    closeDesktop(false)
                } else {
                    openDesktop(false)
                }
                return
            }
            event.preventDefault()
            openMobile()
        })

        link.addEventListener('keydown', (event) => {
            const isSpace = event.key === ' ' || event.key === 'Spacebar'

            if (navBreakpoint.matches) {
                if (event.key === 'ArrowDown' || isSpace) {
                    event.preventDefault()
                    openDesktop(true)
                }
                return
            }

            // Mobile: Enter already opens the panel via the link's native
            // click; Space doesn't activate links the way it does buttons,
            // so without this it'd just scroll the page instead.
            if (isSpace) {
                event.preventDefault()
                openMobile()
            }
        })

        // Escape closes the dropdown and returns focus to its trigger,
        // wherever within it focus currently sits.
        parent.addEventListener('keydown', (event) => {
            if (event.key !== 'Escape') return
            if (navBreakpoint.matches) {
                closeDesktop(true)
            } else {
                closeMobile(true)
            }
        })

        // Desktop only: close once focus moves outside the trigger and its
        // dropdown entirely. Mobile closes only via the back button/Escape,
        // since its panel covers the trigger so focus can't land outside it.
        parent.addEventListener('focusout', (event) => {
            if (!navBreakpoint.matches) return
            const nextFocus = event.relatedTarget
            if (nextFocus && parent.contains(nextFocus)) return
            closeDesktop(false)
        })

        if (backButton) {
            backButton.addEventListener('click', () => {
                if (navBreakpoint.matches) return
                closeMobile(true)
            })
        }

        // Reset mobile menu styles when breakpoint changes.
        navBreakpoint.addEventListener('change', () => {
            closeDropdown(link, dropdown)
            focusTrap.deactivate()
            if (mainTrap) mainTrap.unpause()
        })
    })
}

/**
 * Mobile navigation toggler.
 * @param header
 * @param navToggle
 * @returns {import('focus-trap').FocusTrap} the main menu's own focus trap, so `childMenuToggler` can pause/unpause it around a nested dropdown.
 */
const mobileNavToggler = (header, navToggle) => {
    const headerContainer = header.querySelector('.js-header-container')
    const logo = headerContainer.querySelector('.nav-logo')
    const nav = header.querySelector('.nav-menu-panel')
    const navScroller = nav.querySelector(':scope > div')
    const focusTrap = createFocusTrap(headerContainer, {
        escapeDeactivates: false,
        allowOutsideClick: true,
        preventScroll: true,
    })

    // Reset both drawers back to the top on close, so reopening the menu
    // never lands mid-scroll from a previous visit.
    const resetDrawerScrollPositions = () => {
        if (navScroller) navScroller.scrollTop = 0
        header.querySelectorAll('.js-nav-dropdown').forEach((dropdown) => {
            dropdown.scrollTop = 0
        })
    }

    // Desktop lays the nav out inline, always interactive, regardless of
    // `menu-open` - only mobile hides it off-screen and needs it inert
    // while closed.
    nav.inert = !navBreakpoint.matches

    const visibleLabel = navToggle.querySelector('.js-nav-main-toggler-label')

    // `aria-label` alone drives the accessible name here (and already matches
    // the visible text), so there's no separate screen-reader-only copy to
    // keep in sync alongside it.
    const setTogglerLabel = (isOpen) => {
        navToggle.setAttribute('aria-label', isOpen ? 'Close' : 'Menu')
        if (visibleLabel) visibleLabel.textContent = isOpen ? 'Close' : 'Menu'
    }

    navToggle.addEventListener('click', () => {
        const expanded = navToggle.getAttribute('aria-expanded')
        const willOpen = expanded === 'false'
        navToggle.setAttribute('aria-expanded', `${willOpen}`)
        setTogglerLabel(willOpen)
        BODY.classList.toggle('menu-open')

        if (willOpen) {
            nav.inert = false
            // The logo sits before the nav in `.js-header-container`'s DOM
            // order, so trapping the whole container would otherwise let Tab
            // from the toggler (the last tabbable element) wrap around to
            // the logo instead of back to the nav's own first link. Taking
            // it out of the tab order for as long as the trap is active
            // fixes that without needing a narrower/multi-element trap.
            if (logo) logo.setAttribute('tabindex', '-1')
            focusTrap.activate()
        } else {
            nav.inert = true
            if (logo) logo.removeAttribute('tabindex')
            // Reset any open top-level dropdown(s) and every focus trap -
            // including the ones `childMenuToggler` paused - before the
            // whole menu itself closes.
            header.querySelectorAll('.js-nav-link[aria-expanded="true"]').forEach((activeLink) => {
                const dropdown = getDropdown(activeLink)
                if (dropdown) closeDropdown(activeLink, dropdown)
            })
            focusTraps.forEach((trap) => trap.unpause().deactivate())
            focusTrap.deactivate()
            resetDrawerScrollPositions()
        }
    })

    // Keep `inert` in sync on every breakpoint change - desktop must never
    // have it (the nav is always laid out inline there), mobile needs it
    // whenever the menu isn't currently open.
    navBreakpoint.addEventListener('change', (e) => {
        nav.inert = !e.matches && !BODY.classList.contains('menu-open')

        // Removing `menu-open` alone is enough for the rest - the CSS's
        // `body.menu-open &` rule is what applies the open-state translate,
        // so clearing the class reverts the panel to its base (closed,
        // off-screen) state on its own.
        if (!e.matches) return
        if (!BODY.classList.contains('menu-open')) return

        navToggle.setAttribute('aria-expanded', 'false')
        setTogglerLabel(false)
        if (logo) logo.removeAttribute('tabindex')
        BODY.classList.remove('menu-open')
        focusTrap.deactivate()
        resetDrawerScrollPositions()
    })

    return focusTrap
}

const headerInit = () => {
    const header = document.querySelector('.js-header')
    if (!header) return

    const navToggle = header.querySelector('.js-nav-main-toggler')
    if (!navToggle) return

    const mainTrap = mobileNavToggler(header, navToggle)

    const links = [...header.querySelectorAll('.js-nav-link')]
    if (links.length > 0) {
        childMenuToggler(header, links, mainTrap)
        activeHeaderItem(links)
        accessibleMenuItems(header, links)
    }
}
headerInit()
